import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './RefundRequest.css';
import usePricing from '../hooks/usePricing';


const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const INITIAL = {
  full_name: '',
  email: '',
  phone: '',
  booking_ref: '',
  first_lesson_date: '',
  reason: '',
  additional_notes: '',
  bank_account_name: '',
  bank_sort_code: '',
  bank_account_number: '',
};

// Auto-format sort code: XX-XX-XX
const formatSortCode = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

export default function RefundRequest() {
  const { pricing } = usePricing();
  const [form, setForm]             = useState(INITIAL);
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(null); // { ref_number }
  const [emailChecked, setEmailChecked] = useState(false);
  const [bookingRefChecked, setBookingRefChecked] = useState(false);

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  // Sort code: auto-format on change
  const handleSortCodeChange = (raw) => {
    set('bank_sort_code', formatSortCode(raw));
  };

  // Validate booking reference + email together on blur
  const handleBookingRefBlur = async () => {
    const refVal = form.booking_ref.trim();
    if (!refVal) return;
    // Format check: must be FT followed by digits only
    if (!/^FT\d{5}$/i.test(refVal)) {
      setErrors(e => ({ ...e, booking_ref: 'Invalid format. Your reference is 7 characters: FT followed by exactly 5 digits (e.g. FT47382).' }));
      return;
    }
    if (bookingRefChecked) return;
    const emailVal = form.email.trim().toLowerCase();
    try {
      const params = { ref: refVal };
      if (emailVal && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) params.email = emailVal;
      const res = await axios.get(`${API_BASE}/refunds/validate-booking-ref`, { params });
      if (res.data.refunded) {
        setErrors(e => ({ ...e, booking_ref: res.data.message || 'A refund has already been processed for this booking reference.' }));
      } else if (res.data.found === false) {
        setErrors(e => ({ ...e, booking_ref: res.data.message || 'Reference not found. Please double-check your booking confirmation email.' }));
      } else {
        setErrors(e => ({ ...e, booking_ref: '' }));
      }
      setBookingRefChecked(true);
    } catch (_) {
      // Non-blocking
    }
  };

  // Validate email against backend on blur
  const handleEmailBlur = async () => {
    const emailVal = form.email.trim().toLowerCase();
    if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) return;
    if (emailChecked) return;
    try {
      const res = await axios.get(`${API_BASE}/refunds/validate-email`, { params: { email: emailVal } });
      if (res.data.cancelled) {
        setErrors(e => ({
          ...e,
          email: res.data.message || 'Your booking has already been refunded and cancelled. You cannot submit another refund request.'
        }));
      } else if (res.data.found === false) {
        setErrors(e => ({
          ...e,
          email: 'We couldn\'t find a booking for this email address. Please use the email you booked with.'
        }));
      } else {
        setErrors(e => ({ ...e, email: '' }));
      }
      setEmailChecked(true);
      // If ref is already filled, re-validate it against the new email
      if (form.booking_ref.trim()) {
        setBookingRefChecked(false);
      }
    } catch (_) {
      // Non-blocking — allow form submission even if lookup fails
    }
  };

  const validate = () => {
    const e = {};

    // Full name — required, no digits
    if (!form.full_name.trim()) {
      e.full_name = 'Full name is required.';
    } else if (/\d/.test(form.full_name)) {
      e.full_name = 'Name must not contain numbers.';
    } else if (form.full_name.trim().length < 2) {
      e.full_name = 'Please enter your full name.';
    }

    // Email
    if (!form.email.trim()) {
      e.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Enter a valid email address.';
    }

    // Phone — optional but validate format if provided
    if (form.phone.trim() && !/^[\d\s\+\-\(\)]{7,15}$/.test(form.phone.trim())) {
      e.phone = 'Enter a valid phone number.';
    }

    // Booking reference — required, must be FT + digits
    if (!form.booking_ref.trim()) {
      e.booking_ref = 'Your order reference is required. Check your booking confirmation email (e.g. FT47382).';
    } else if (!/^FT\d{5}$/i.test(form.booking_ref.trim())) {
      e.booking_ref = 'Invalid format. Your reference is 7 characters: FT followed by exactly 5 digits (e.g. FT47382).';
    }

    // Reason
    if (!form.reason.trim()) e.reason = 'Please describe your reason for the refund.';

    // Bank account holder name — required, no digits
    if (!form.bank_account_name.trim()) {
      e.bank_account_name = 'Account holder name is required.';
    } else if (/\d/.test(form.bank_account_name)) {
      e.bank_account_name = 'Account name must not contain numbers.';
    }

    // Sort code — XX-XX-XX format
    const sc = form.bank_sort_code.trim();
    if (!sc) {
      e.bank_sort_code = 'Sort code is required.';
    } else if (!/^\d{2}-\d{2}-\d{2}$/.test(sc)) {
      e.bank_sort_code = 'Sort code must be in format XX-XX-XX (e.g. 12-34-56).';
    }

    // Account number — exactly 8 digits
    const an = form.bank_account_number.replace(/\s/g, '');
    if (!an) {
      e.bank_account_number = 'Account number is required.';
    } else if (!/^\d{8}$/.test(an)) {
      e.bank_account_number = 'Account number must be exactly 8 digits.';
    }

    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();

    // Always run API checks on submit (catches fast-clickers who skip blur)
    const emailVal = form.email.trim().toLowerCase();
    const refVal   = form.booking_ref.trim();

    if (!errs.email && emailVal && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      if (!emailChecked) {
        try {
          const res = await axios.get(`${API_BASE}/refunds/validate-email`, { params: { email: emailVal } });
          if (res.data.cancelled) {
            errs.email = res.data.message || 'Your booking has already been refunded and cancelled. You cannot submit another refund request.';
          } else if (res.data.found === false) {
            errs.email = 'We couldn\'t find a booking for this email address. Please use the email you booked with.';
          }
          setEmailChecked(true);
        } catch (_) { /* non-blocking */ }
      } else if (errors.email) {
        errs.email = errors.email;
      }
    }

    if (!errs.booking_ref && refVal && /^FT\d{5}$/i.test(refVal)) {
      if (!bookingRefChecked) {
        try {
          const params = { ref: refVal };
          if (emailVal && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) params.email = emailVal;
          const res = await axios.get(`${API_BASE}/refunds/validate-booking-ref`, { params });
          if (res.data.refunded) {
            errs.booking_ref = res.data.message || 'A refund has already been processed for this booking reference.';
          } else if (res.data.found === false) {
            errs.booking_ref = res.data.message || 'Reference not found. Please double-check your booking confirmation email.';
          }
          setBookingRefChecked(true);
        } catch (_) { /* non-blocking */ }
      } else if (errors.booking_ref) {
        errs.booking_ref = errors.booking_ref;
      }
    }

    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/refunds`, form);
      if (res.data.success) {
        setSubmitted({ ref_number: res.data.ref_number });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrors({ general: res.data.message || 'Submission failed. Please try again.' });
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.field) {
        setErrors({ [errData.field]: errData.message });
      } else {
        setErrors({ general: errData?.message || 'Server error. Please try again later.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rfq-page">
        <div className="rfq-success-wrap">
          <div className="rfq-success-icon">
            <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="32" cy="32" r="28"/>
              <path d="M20 33l8 8 16-16" strokeWidth="3"/>
            </svg>
          </div>
          <h1 className="rfq-success-title">Refund Request Submitted</h1>
          <p className="rfq-success-msg">
            Thank you. Your request has been received and will be reviewed by our team within <strong>3–5 working days</strong>.
          </p>
          <div className="rfq-ref-box">
            <span className="rfq-ref-label">Your reference number</span>
            <span className="rfq-ref-num">{submitted.ref_number}</span>
            <span className="rfq-ref-hint">Please keep this for your records.</span>
          </div>
          <p className="rfq-success-note">
            We will contact you at <strong>{form.email}</strong> once your request has been reviewed.
            If you have any questions in the meantime, please <Link to="/contact">contact us</Link>.
          </p>
          <p className="rfq-success-note" style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: '0.25rem' }}>
            📬 Don't see our email? Check your <strong>spam or junk folder</strong>.
          </p>
          <Link to="/" className="rfq-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rfq-page">
      {/* Header */}
      <div className="rfq-hero">
        <div className="rfq-hero-inner container">
          <span className="rfq-eyebrow">Student Refunds</span>
          <h1 className="rfq-hero-title">Request a Refund</h1>
          <p className="rfq-hero-sub">
            Please read our <Link to="/pricing#refund-policy">refund policy</Link> before submitting. Refunds are only available after
            your first 2-hour session and before any subsequent lessons begin.
          </p>
        </div>
      </div>

      <div className="container rfq-container">
        <div className="rfq-policy-banner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div>
            <strong>Refund Policy Reminder:</strong> Refunds are only available if requested immediately after your first 2-hour session.
            Once subsequent lessons begin, the full package becomes non-refundable. Unused hours will be refunded minus completed hours
            at the £{pricing.hourly_rate} standard rate. We only refund fees paid to us — not payments made directly to instructors.
          </div>
        </div>

        <form className="rfq-form" onSubmit={handleSubmit} noValidate>

          {/* ── Section 1: Your Details ── */}
          <div className="rfq-section">
            <h2 className="rfq-section-title">
              <span className="rfq-section-num">1</span>
              Your Details
            </h2>
            <div className="rfq-grid rfq-grid--3">
              <div className={`rfq-field${errors.full_name ? ' rfq-field--error' : ''}`}>
                <label>Full Name <span className="rfq-req">*</span></label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={e => set('full_name', e.target.value)}
                  placeholder="Jane Smith"
                />
                {errors.full_name && <span className="rfq-err">{errors.full_name}</span>}
              </div>
              <div className={`rfq-field${errors.email ? ' rfq-field--error' : ''}`}>
                <label>Email Address <span className="rfq-req">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => { set('email', e.target.value); setEmailChecked(false); }}
                  onBlur={handleEmailBlur}
                  placeholder="jane@example.com"
                />
                <span className="rfq-hint">Use the same email you used when booking</span>
                {errors.email && <span className="rfq-err">{errors.email}</span>}
              </div>
              <div className={`rfq-field${errors.phone ? ' rfq-field--error' : ''}`}>
                <label>Phone Number <span className="rfq-opt">(optional)</span></label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="07700 000000"
                />
                {errors.phone && <span className="rfq-err">{errors.phone}</span>}
              </div>
            </div>
          </div>

          {/* ── Section 2: Booking Details ── */}
          <div className="rfq-section">
            <h2 className="rfq-section-title">
              <span className="rfq-section-num">2</span>
              Booking Details
            </h2>
            <div className="rfq-grid rfq-grid--3">
              <div className={`rfq-field${errors.booking_ref ? ' rfq-field--error' : ''}`}>
                <label>Order / Booking Reference <span className="rfq-req">*</span></label>
                <input
                  type="text"
                  value={form.booking_ref}
                  onChange={e => { set('booking_ref', e.target.value); setBookingRefChecked(false); }}
                  onBlur={handleBookingRefBlur}
                  placeholder="e.g. FT47382"
                  maxLength={7}
                />
                <span className="rfq-hint">Found in your booking confirmation email — required to verify your booking</span>
                {errors.booking_ref && <span className="rfq-err">{errors.booking_ref}</span>}
              </div>
              <div className="rfq-field rfq-field--span2">
                <label>Date of First Lesson <span className="rfq-opt">(optional)</span></label>
                <input
                  type="date"
                  value={form.first_lesson_date}
                  onChange={e => set('first_lesson_date', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Section 3: Reason ── */}
          <div className="rfq-section">
            <h2 className="rfq-section-title">
              <span className="rfq-section-num">3</span>
              Reason for Refund
            </h2>
            <div className={`rfq-field${errors.reason ? ' rfq-field--error' : ''}`}>
              <label>Reason <span className="rfq-req">*</span></label>
              <textarea
                value={form.reason}
                onChange={e => set('reason', e.target.value)}
                rows={4}
                placeholder="Please explain why you are requesting a refund..."
              />
              {errors.reason && <span className="rfq-err">{errors.reason}</span>}
            </div>
            <div className="rfq-field" style={{ marginTop: '16px' }}>
              <label>Additional Notes <span className="rfq-opt">(optional)</span></label>
              <textarea
                value={form.additional_notes}
                onChange={e => set('additional_notes', e.target.value)}
                rows={3}
                placeholder="Any other information you'd like us to know..."
              />
            </div>
          </div>

          {/* ── Section 4: Bank Details ── */}
          <div className="rfq-section rfq-section--bank">
            <h2 className="rfq-section-title">
              <span className="rfq-section-num">4</span>
              Bank Details for Refund
            </h2>
            <p className="rfq-bank-note">
              We will use these details to process your refund if approved. Please double-check the information below.
            </p>
            <div className="rfq-grid rfq-grid--3">
              <div className={`rfq-field${errors.bank_account_name ? ' rfq-field--error' : ''}`}>
                <label>Account Holder Name <span className="rfq-req">*</span></label>
                <input
                  type="text"
                  value={form.bank_account_name}
                  onChange={e => set('bank_account_name', e.target.value)}
                  placeholder="Full name on account"
                />
                <span className="rfq-hint">Exactly as it appears on your bank account</span>
                {errors.bank_account_name && <span className="rfq-err">{errors.bank_account_name}</span>}
              </div>
              <div className={`rfq-field${errors.bank_sort_code ? ' rfq-field--error' : ''}`}>
                <label>Sort Code <span className="rfq-req">*</span></label>
                <input
                  type="text"
                  value={form.bank_sort_code}
                  onChange={e => handleSortCodeChange(e.target.value)}
                  placeholder="12-34-56"
                  maxLength={8}
                  inputMode="numeric"
                />
                <span className="rfq-hint">6 digits in XX-XX-XX format</span>
                {errors.bank_sort_code && <span className="rfq-err">{errors.bank_sort_code}</span>}
              </div>
              <div className={`rfq-field${errors.bank_account_number ? ' rfq-field--error' : ''}`}>
                <label>Account Number <span className="rfq-req">*</span></label>
                <input
                  type="text"
                  value={form.bank_account_number}
                  onChange={e => set('bank_account_number', e.target.value.replace(/\D/g, ''))}
                  placeholder="12345678"
                  maxLength={8}
                  inputMode="numeric"
                />
                <span className="rfq-hint">8-digit UK account number</span>
                {errors.bank_account_number && <span className="rfq-err">{errors.bank_account_number}</span>}
              </div>
            </div>
            <div className="rfq-security-note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Your bank details are stored securely and used solely to process your refund if approved.
              We do not store card details or share your information with third parties.
            </div>
          </div>

          {errors.general && (
            <div className="rfq-error-banner">{errors.general}</div>
          )}

          <div className="rfq-submit-row">
            <p className="rfq-submit-note">
              By submitting this form you confirm the information provided is accurate.
            </p>
            <button type="submit" className="rfq-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Refund Request'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
